import Icon, { FilePdfOutlined, ThunderboltOutlined } from '@ant-design/icons';
import {
    Button,
    Col,
    Divider,
    Dropdown,
    Menu,
    message,
    Modal,
    Rate,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tooltip,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import ReactECharts from 'echarts-for-react';
import React, { Component } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, injectIntl, WrappedComponentProps } from 'react-intl';
import { Link, RouteComponentProps } from 'react-router-dom';
import checklistApi from '../../../../../api/ChecklistApi';
import designRatingReportApi from '../../../../../api/DesignRatingReportApi';
import hubApi from '../../../../../api/HubApi';
import projectApi from '../../../../../api/ProjectApi';
import LayoutComponent from '../../../../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../../../../context/CustomContext';
import { Checklist, Hub, Project } from '../../../../../model/entities';
import {
    DesignPerformanceRating,
    DesignRatingReport,
    DesignRatingReportDrTracker,
    DesignRatingReportItem,
    DesignRatingReportResult,
    KeyDesignIssue,
} from '../../../../../model/entities-report';
import { ReactComponent as BackSvg } from '../../../../../resources/images/back.svg';
import { ReactComponent as CaretDownSvg } from '../../../../../resources/images/caret-down.svg';
import { ReactComponent as SaveSvg } from '../../../../../resources/images/save.svg';
import { ReactComponent as TrashCanSvg } from '../../../../../resources/images/trash-can.svg';
import { ReactComponent as WarningSvg } from '../../../../../resources/images/warning.svg';
import notificationService from '../../../../../services/NotificationService';
import stringService from '../../../../../services/StringService';
import DesignRatingReportModal from './DesignRatingReportModal/DesignRatingReportModal';
import styles from './DesignRatingReportPage.module.scss';

class DesignRatingReportPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;

    constructor(props: Props) {
        super(props);
        this.state = {
            hubs: [],
            projects: [],
            checklists: [],
        };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    /**
     * Initialiazes the page.
     */
    init = async () => {
        try {
            if (this.props.match.params.id === 'new') {
                await this.new();
            } else {
                await this.get(+this.props.match.params.id);
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Initialiazes the page for report creation.
     */
    new = async () => {
        this.setState({ loading: 'loadingHubs' });
        const hubs = await hubApi.list(true);
        const hub = hubs[0];
        const projects = await projectApi.list(hub.provider, hub.id);
        const report: DesignRatingReport = {};
        this.setState({ hubs, hub, projects, report });
    };

    /**
     * Initialiazes the page for report edition.
     */
    get = async (id: number) => {
        this.setState({ loading: 'loading' });

        let hubs: Hub[];
        let hub: Hub | undefined;
        let projects: Project[] = [];
        let project: Project | undefined;
        let checklists: Checklist[] = [];
        let checklist: Checklist | undefined;

        const report = await designRatingReportApi.get(id);
        hubs = await hubApi.list(true);
        if (hubs.map((h) => h.id).includes(report.hubId!)) {
            hub = hubs.filter((h) => h.id === report.hubId)[0];
            projects = await projectApi.list(hub.provider, hub.id);
            if (projects.map((p) => p.id).includes(report.projectId!)) {
                project = projects.filter((p) => p.id === report.projectId)[0];
                checklists = (await checklistApi.list(0, 100, 'title', true, hub!.id, project.id!)).content;
                if (checklists.map((c) => c.id).includes(report.checklistId!)) {
                    checklist = checklists.filter((c) => c.id === report.checklistId)[0];
                }
            }
        }

        this.setState({ report, hubs, hub, projects, project, checklists, checklist });
    };

    /**
     * Selects a hub.
     * @param hubId - the hub id
     */
    selectHub = async (hubId: string) => {
        const { hubs } = this.state;
        try {
            this.setState({ loading: 'loadingProjects' });
            const hub = hubs.find((p) => p.id === hubId);
            const projects = await projectApi.list(hub!.provider, hub!.id);
            const project = undefined;
            const checklists: Checklist[] = [];
            const checklist = undefined;
            this.setState({ hub, projects, project, checklists, checklist });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Selects a project.
     * @param projectId - the project id
     */
    selectProject = async (projectId: string) => {
        const { hub, projects } = this.state;
        try {
            this.setState({ loading: 'loadingChecklists' });
            const project = projects.find((p) => p.id === projectId);
            const checklists = (await checklistApi.list(0, 100, 'title', true, hub!.id, project!.id)).content;
            const checklist = undefined;
            this.setState({ project, checklists, checklist });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Selects a checklist.
     * @param checklistId - the checklist id
     */
    selectChecklist = async (checklistId: string) => {
        const { checklists } = this.state;
        try {
            const checklist = checklists.find((c) => c.id === checklistId);
            this.setState({ checklist });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    /**
     * Returns a report.
     */
    build = async () => {
        const { hub, project, checklist } = this.state;
        try {
            this.setState({ loading: 'loading' });
            const result = await designRatingReportApi.build(hub!.id, project!.id, checklist!.id);
            const report: DesignRatingReport = Object.assign({}, this.state.report, {
                result,
                hubId: hub?.id,
                projectId: project?.id,
                checklistId: checklist?.id,
            });

            this.setState({ report });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Saves the report.
     * @param values  - the report values
     */
    save = async (values: any) => {
        try {
            let report: DesignRatingReport = Object.assign({}, this.state.report, values);
            report.result = undefined;
            report = report.id
                ? await designRatingReportApi.update(report)
                : await designRatingReportApi.create(report);
            report.result = this.state.report?.result;
            message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
            this.showSaveModal(false);
            this.setState({ report });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    /**
     * Deletes the report.
     */
    delete = async () => {
        const { report } = this.state;

        try {
            await designRatingReportApi.delete(report!.id!);
            this.props.history.push(`/reports/design-ratings`);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Returns the alignment of an item based in the type of its value.
     * @param item - the item
     * @returns the alignment
     */
    getItemAlignment = (item?: DesignRatingReportItem): 'left' | 'center' | 'right' => {
        if (item && item.valueList) {
            return 'left';
        } else if (item && item.valueNumber) {
            return 'right';
        } else if (item && item.valueDate) {
            return 'center';
        } else {
            return 'left';
        }
    };

    /**
     * Shows the save report modal.
     */
    showSaveModal = (visible: boolean) => {
        const saveModalVisible = visible;
        this.setState({ saveModalVisible });
    };

    /**
     * Shows the delete report modal.
     */
    showDeleteModal = () => {
        const { intl } = this.props;
        Modal.confirm({
            title: intl.formatMessage({ id: 'reports.delete.modal.title' }),
            icon: <Icon component={WarningSvg} />,
            okText: intl.formatMessage({ id: 'button.yes' }),
            okType: 'danger',
            cancelText: intl.formatMessage({ id: 'button.no' }),
            onOk: () => this.delete(),
        });
    };

    /**
     * Returns if the save button is disabled.
     * @returns if the save button is disabled
     */
    isSaveButtonDisabled = (): boolean => {
        const { report } = this.state;
        return !report || !report.result;
    };

    /**
     * Returns if the delete button is disabled.
     * @returns if the delete button is disabled
     */
    isDeleteButtonDisabled = (): boolean => {
        const { report } = this.state;
        return !report || !report.id;
    };

    /*** COMPONENTS ***/

    renderSelector = (): React.ReactElement | undefined => {
        const { hubs, hub, projects, project, checklists, checklist, loading, report } = this.state;
        const hubOptions = hubs.map((hub) => (
            <Select.Option key={hub.id} value={hub.id}>
                {`${hub.provider} > ${hub.name}`}
            </Select.Option>
        ));
        const projectOptions = projects
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((project) => (
                <Select.Option key={project.id} value={project.id}>
                    {project.name}
                </Select.Option>
            ));
        const checklistOptions = checklists
            .sort((a, b) => a.title.localeCompare(b.title))
            .map((checklist) => (
                <Select.Option key={checklist.id} value={checklist.id}>
                    {checklist.identifier} - {checklist.title}
                </Select.Option>
            ));

        return (
            <div className={styles.selector}>
                <Row gutter={[48, 24]} align="bottom">
                    <Col span={7} className={styles.field}>
                        <FormattedMessage id="reports.hub" tagName="label" />
                        <Select
                            size="large"
                            className={styles.hubs}
                            value={hub?.id}
                            onChange={this.selectHub}
                            placeholder={<FormattedMessage id="reports.hub.placeholder" />}
                            loading={loading === 'loadingHubs'}
                            disabled={report && !!report.id}
                        >
                            {hubOptions}
                        </Select>
                    </Col>
                    <Col span={10} className={styles.field}>
                        <FormattedMessage id="reports.project" tagName="label" />
                        <Select
                            size="large"
                            className={styles.projects}
                            value={project?.id}
                            onChange={this.selectProject}
                            placeholder={<FormattedMessage id="reports.project.placeholder" />}
                            showSearch
                            filterOption={stringService.filterOptions}
                            loading={loading === 'loadingProjects'}
                            disabled={report && !!report.id}
                        >
                            {projectOptions}
                        </Select>
                    </Col>
                    <Col span={7} className={styles.button}>
                        <Button
                            size="large"
                            type={this.isSaveButtonDisabled() ? 'primary' : 'default'}
                            icon={<ThunderboltOutlined />}
                            onClick={this.build}
                            disabled={!checklist || (report && !!report.id)}
                            block
                        >
                            <FormattedMessage id="reports.create.button" tagName="span" />
                        </Button>
                    </Col>
                    <Col span={17} className={styles.field}>
                        <FormattedMessage id="reports.checklist" tagName="label" />
                        <Select
                            size="large"
                            className={styles.checklists}
                            value={checklist?.id}
                            onChange={this.selectChecklist}
                            placeholder={<FormattedMessage id="reports.checklist.placeholder" />}
                            showSearch
                            filterOption={stringService.filterOptions}
                            loading={loading === 'loadingChecklists'}
                            disabled={report && !!report.id}
                        >
                            {checklistOptions}
                        </Select>
                    </Col>
                </Row>

                <Divider />
            </div>
        );
    };

    renderToolbar = (): React.ReactElement | undefined => {
        const { report, loading } = this.state;

        return (
            <div className="toolbar report">
                <div className="title">
                    <h2>{report?.name || <FormattedMessage id="designRatingReport.title" />}</h2>
                </div>
                <Space>
                    <Tooltip title={<FormattedMessage id="button.back" />}>
                        <Link to="/reports/design-ratings">
                            <Button type="ghost" size="large" icon={<Icon component={BackSvg} />} />
                        </Link>
                    </Tooltip>
                    <Tooltip title={<FormattedMessage id="button.exportPdf" />}>
                        <Button
                            size="large"
                            icon={<FilePdfOutlined />}
                            loading={loading === 'exporting'}
                            disabled
                        ></Button>
                    </Tooltip>
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        disabled={this.isSaveButtonDisabled()}
                        overlay={
                            <Menu>
                                <Menu.Item
                                    key="company"
                                    icon={<Icon component={SaveSvg} />}
                                    onClick={() => this.showSaveModal(true)}
                                    disabled={this.isSaveButtonDisabled()}
                                >
                                    <FormattedMessage id="button.save" />
                                </Menu.Item>
                                <Menu.Item
                                    key="inactivate"
                                    icon={<Icon component={TrashCanSvg} />}
                                    onClick={this.showDeleteModal}
                                    disabled={this.isDeleteButtonDisabled()}
                                >
                                    <FormattedMessage id="button.delete" />
                                </Menu.Item>
                            </Menu>
                        }
                    >
                        <Button type="primary" size="large">
                            <FormattedMessage id="button.actions" /> <Icon component={CaretDownSvg} />
                        </Button>
                    </Dropdown>
                </Space>
            </div>
        );
    };

    renderSaveModal = (): React.ReactElement | undefined => {
        const { report, saveModalVisible } = this.state;

        return (
            <DesignRatingReportModal
                visible={saveModalVisible}
                report={report}
                onSave={this.save}
                onCancel={() => this.showSaveModal(false)}
            />
        );
    };

    renderItemValue = (item?: DesignRatingReportItem): React.ReactElement | undefined => {
        if (item && item.valueList) {
            return <>{item.valueList.join(', ')}</>;
        } else if (item && item.valueNumber) {
            return <>{item.valueNumber}</>;
        } else if (item && item.valueDate) {
            return <FormattedDate value={item.valueDate as any} dateStyle="short" />;
        } else if (item) {
            return <>{item.valueText}</>;
        }
    };

    renderSiteGeneralDataTableHeader = () => {
        return (
            <div className={styles.header}>
                <h3>
                    <FormattedMessage id="designRatingReport.siteGeneralData.header.title" />
                </h3>
                <FormattedMessage id="designRatingReport.siteGeneralData.header.description" />
            </div>
        );
    };

    renderSiteGeneralDataTable = (result: DesignRatingReportResult): React.ReactElement | undefined => {
        const columns: ColumnsType<DesignRatingReportResult> = result.siteGeneralData.map((item) => ({
            title: item.title,
            key: item.title,
            align: this.getItemAlignment(item),
            width: 50,
            render: (value: string, report: DesignRatingReportResult) => {
                const currentItem = report.siteGeneralData.find((i) => i.title === item.title);
                return this.renderItemValue(currentItem);
            },
        }));

        return (
            <div className={styles.section}>
                <Table
                    title={() => this.renderSiteGeneralDataTableHeader()}
                    dataSource={[result]}
                    columns={columns}
                    pagination={false}
                    rowKey="title"
                />
            </div>
        );
    };

    renderDRTrackerTableHeader = () => {
        return (
            <div className={styles.header}>
                <h3>
                    <FormattedMessage id="designRatingReport.drTracker.header.title" />
                </h3>
                <FormattedMessage id="designRatingReport.drTracker.header.description" />
            </div>
        );
    };

    renderDrTrackerTable = (result: DesignRatingReportResult): React.ReactElement | undefined => {
        if (result.drTrackers && result.drTrackers.length > 0) {
            const itemColumns: ColumnsType<DesignRatingReportDrTracker> = result.drTrackers[0].items.map(
                (item, index) => ({
                    title: item.title,
                    key: item.title,
                    align: this.getItemAlignment(item),
                    width: 50,
                    render: (value: string, drTracker: DesignRatingReportDrTracker) => {
                        const currentItem = drTracker.items[index];
                        return this.renderItemValue(currentItem);
                    },
                }),
            );
            const columns: ColumnsType<DesignRatingReportDrTracker> = [
                {
                    title: <FormattedMessage id="designRatingReport.drTracker.title" />,
                    dataIndex: 'title',
                    key: 'title',
                    width: 40,
                },
                {
                    title: <FormattedMessage id="designRatingReport.drTracker.milestone" />,
                    dataIndex: 'milestone',
                    key: 'milestone',
                    width: 60,
                },
                ...itemColumns,
            ];

            return (
                <div className={styles.section}>
                    <Table
                        title={() => this.renderDRTrackerTableHeader()}
                        dataSource={result.drTrackers}
                        columns={columns}
                        pagination={false}
                        rowKey={(dr) => dr.title + dr.milestone}
                    />
                </div>
            );
        }
    };

    renderKeyDesignIssueTableHeader = () => {
        return (
            <div className={styles.header}>
                <h3>
                    <FormattedMessage id="designRatingReport.keyDesignIssueTable.header.title" />
                </h3>
                <FormattedMessage id="designRatingReport.keyDesignIssueTable.header.description" />
            </div>
        );
    };

    renderKeyDesignIssueTable = (result: DesignRatingReportResult): React.ReactElement | undefined => {
        if (result.plotSuitabilityRating.keyDesignIssues) {
            const columns: ColumnsType<KeyDesignIssue> = [
                {
                    title: <FormattedMessage id="designRatingReport.plotSuitabilityRating.keyDesignIssues.name" />,
                    dataIndex: 'name',
                    key: 'name',
                    align: 'center',
                    width: 160,
                },
                {
                    title: (
                        <FormattedMessage id="designRatingReport.plotSuitabilityRating.keyDesignIssues.description" />
                    ),
                    dataIndex: 'description',
                    key: 'description',
                },
                {
                    title: (
                        <FormattedMessage id="designRatingReport.plotSuitabilityRating.keyDesignIssues.standardValue" />
                    ),
                    dataIndex: 'standardValue',
                    key: 'standardValue',
                    align: 'right',
                    width: 160,
                },
                {
                    title: <FormattedMessage id="designRatingReport.plotSuitabilityRating.keyDesignIssues.plotValue" />,
                    dataIndex: 'plotValue',
                    key: 'plotValue',
                    align: 'right',
                    width: 120,
                },
                {
                    title: <FormattedMessage id="designRatingReport.plotSuitabilityRating.keyDesignIssues.score" />,
                    dataIndex: 'score',
                    key: 'score',
                    align: 'right',
                    width: 120,
                },
                {
                    title: <FormattedMessage id="designRatingReport.plotSuitabilityRating.total" />,
                    key: 'total',
                    align: 'center',
                    width: 120,
                    render: (value: any, record: KeyDesignIssue, index: number) => {
                        const rows = result.plotSuitabilityRating.keyDesignIssues.length;
                        if (index === 0) {
                            return result.plotSuitabilityRating.total;
                        } else if (index === 2) {
                            return result.plotSuitabilityRating.rating;
                        } else if (index === rows - 2) {
                            return <Rate allowHalf value={result.plotSuitabilityRating.rating} />;
                        }
                    },
                    onCell: (kdi, index) => {
                        const rows = result.plotSuitabilityRating.keyDesignIssues.length;
                        if (index === 0) {
                            return { rowspan: 2 } as React.HTMLAttributes<HTMLElement>;
                        } else if (index === 1) {
                            return { hidden: true } as React.HTMLAttributes<HTMLElement>;
                        } else if (index === 2) {
                            return { rowspan: rows - 4, colspan: 2 } as React.HTMLAttributes<HTMLElement>;
                        } else if (index === rows - 2) {
                            return { rowspan: 2, colspan: 2 } as React.HTMLAttributes<HTMLElement>;
                        } else {
                            return { hidden: true } as React.HTMLAttributes<HTMLElement>;
                        }
                    },
                },
                {
                    title: <FormattedMessage id="designRatingReport.plotSuitabilityRating.benchmark" />,
                    dataIndex: 'benchmark',
                    key: 'benchmark',
                    align: 'center',
                    width: 120,
                    render: () => result.plotSuitabilityRating.benchmark,
                    onCell: (_, index) => {
                        if (index === 0) {
                            return { rowspan: 2 } as React.HTMLAttributes<HTMLElement>;
                        } else if (index === 1) {
                            return { hidden: true } as React.HTMLAttributes<HTMLElement>;
                        } else {
                            return { hidden: true } as React.HTMLAttributes<HTMLElement>;
                        }
                    },
                },
            ];

            return (
                <div className={styles.section}>
                    <Table
                        title={() => this.renderKeyDesignIssueTableHeader()}
                        dataSource={result.plotSuitabilityRating.keyDesignIssues}
                        columns={columns}
                        pagination={false}
                        rowKey="name"
                        size="small"
                    />
                </div>
            );
        }
    };

    renderDesignPerformanceRatingHeader = () => {
        return (
            <div className={styles.header}>
                <h3>
                    <FormattedMessage id="designRatingReport.designPerformanceRating.header.title" />
                </h3>
            </div>
        );
    };

    renderDesignPerformanceRating = (result: DesignRatingReportResult): React.ReactElement | undefined => {
        const { intl } = this.props;

        if (result.designPerformanceRating) {
            const columns: ColumnsType<DesignPerformanceRating> = [
                {
                    title: <FormattedMessage id="designRatingReport.designPerformanceRating.total" />,
                    dataIndex: 'total',
                    key: 'total',
                    align: 'center',
                    width: 160,
                    render: (value: any, record: DesignPerformanceRating, index: number) => {
                        if (index === 0) {
                            return value;
                        } else if (index === 1) {
                            return <FormattedMessage id="designRatingReport.designPerformanceRating.difference" />;
                        } else if (index === 2) {
                            return <FormattedMessage id="designRatingReport.designPerformanceRating.rating" />;
                        } else if (index === 3) {
                            return <FormattedMessage id="designRatingReport.designPerformanceRating.flags" />;
                        }
                    },
                },
                {
                    title: <FormattedMessage id="designRatingReport.designPerformanceRating.benchmark" />,
                    dataIndex: 'benchmark',
                    key: 'benchmark',
                    align: 'center',
                    width: 160,
                    render: (value: any, record: DesignPerformanceRating, index: number) => {
                        if (index === 0) {
                            return value;
                        } else if (index === 1) {
                            return record.difference;
                        } else if (index === 2) {
                            return (
                                <FormattedNumber
                                    value={record.rating}
                                    maximumFractionDigits={2}
                                    minimumFractionDigits={2}
                                />
                            );
                        } else if (index === 3) {
                            return record.flags;
                        }
                    },
                },
            ];
            const dataSource: DesignPerformanceRating[] = Array(4).fill(result.designPerformanceRating);

            return (
                <div className={styles.section}>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Table
                                title={() => this.renderDesignPerformanceRatingHeader()}
                                dataSource={dataSource}
                                columns={columns}
                                pagination={false}
                                rowKey="name"
                                size="small"
                            />
                        </Col>
                        <Col span={16}>
                            <ReactECharts
                                option={{
                                    tooltip: {
                                        trigger: 'item',
                                    },
                                    color: ['#ff9900', '#333f50'],
                                    emphasis: {
                                        itemStyle: {
                                            shadowBlur: 10,
                                            shadowOffsetX: 0,
                                            shadowColor: 'rgba(0, 0, 0, 0.5)',
                                        },
                                    },
                                    series: [
                                        {
                                            type: 'pie',
                                            radius: '90%',
                                            data: [
                                                {
                                                    value: result.designPerformanceRating.total,
                                                    name: intl.formatMessage({
                                                        id: 'designRatingReport.designPerformanceRating.achieved',
                                                    }),
                                                },
                                                {
                                                    value: result.designPerformanceRating.difference,
                                                    name: intl.formatMessage({
                                                        id: 'designRatingReport.designPerformanceRating.notAchieved',
                                                    }),
                                                },
                                            ],
                                        },
                                    ],
                                }}
                            />
                        </Col>
                    </Row>
                </div>
            );
        }
    };

    renderCategoryRatingHeader = () => {
        return (
            <div className={`${styles.header} ${styles.detached}`}>
                <h3>
                    <FormattedMessage id="designRatingReport.categoryRating.header.title" />
                </h3>
            </div>
        );
    };

    renderCategoryRating = (result: DesignRatingReportResult): React.ReactElement | undefined => {
        const { intl } = this.props;

        const totals = result.zoneRatings.flatMap((zr) => zr.categoryRatings).map((zr) => zr.total);
        const benchmarks = result.zoneRatings.flatMap((zr) => zr.categoryRatings).map((zr) => zr.benchmark);

        return (
            <div className={styles.section}>
                {this.renderCategoryRatingHeader()}
                <ReactECharts
                    style={{ height: '1300px' }}
                    option={{
                        grid: { left: '220px' },
                        yAxis: {
                            type: 'category',
                            data: result.zoneRatings.flatMap((zr) => zr.categoryRatings).map((zr) => zr.name),
                        },
                        xAxis: {
                            type: 'value',
                        },
                        tooltip: {
                            trigger: 'axis',
                            axisPointer: {
                                type: 'shadow',
                            },
                        },
                        series: [
                            {
                                name: intl.formatMessage({
                                    id: 'designRatingReport.categoryRating.score',
                                }),
                                type: 'bar',
                                label: {
                                    show: true,
                                    position: 'right',
                                },
                                barWidth: 20,
                                data: totals,
                            },
                            {
                                name: intl.formatMessage({
                                    id: 'designRatingReport.categoryRating.benchmark',
                                }),
                                type: 'bar',
                                label: {
                                    show: true,
                                    position: 'right',
                                },
                                barWidth: 20,
                                data: benchmarks,
                            },
                        ],
                        color: ['#ff9900', '#333f50'],
                    }}
                />
            </div>
        );
    };

    renderZoneRatingHeader = () => {
        return (
            <div className={`${styles.header} ${styles.detached}`}>
                <h3>
                    <FormattedMessage id="designRatingReport.zoneRating.header.title" />
                </h3>
            </div>
        );
    };

    renderZoneRating = (result: DesignRatingReportResult): React.ReactElement | undefined => {
        const { intl } = this.props;

        const totals = result.zoneRatings.map((zr) => zr.total);
        const benchmarks = result.zoneRatings.map((zr) => zr.benchmark);

        return (
            <div className={styles.section}>
                {this.renderZoneRatingHeader()}
                <ReactECharts
                    option={{
                        xAxis: {
                            type: 'category',
                            boundaryGap: [0, 0.01],
                            data: result.zoneRatings.map((zr) => zr.name),
                        },
                        yAxis: {
                            type: 'value',
                        },
                        tooltip: {
                            trigger: 'axis',
                            axisPointer: {
                                type: 'shadow',
                            },
                        },
                        series: [
                            {
                                name: intl.formatMessage({
                                    id: 'designRatingReport.zoneRating.score',
                                }),
                                type: 'bar',
                                label: {
                                    show: true,
                                    position: 'top',
                                },
                                data: totals,
                            },
                            {
                                name: intl.formatMessage({
                                    id: 'designRatingReport.zoneRating.benchmark',
                                }),
                                type: 'bar',
                                label: {
                                    show: true,
                                    position: 'top',
                                },
                                data: benchmarks,
                            },
                        ],
                        color: ['#ff9900', '#333f50'],
                    }}
                />
            </div>
        );
    };

    renderReport = (): React.ReactElement | undefined => {
        const { report, loading } = this.state;

        if (loading === 'loading') {
            return (
                <div className={styles.spin}>
                    <Spin size="large" />
                </div>
            );
        } else if (report && report.result) {
            return (
                <div>
                    {this.renderSiteGeneralDataTable(report.result)}
                    {this.renderDrTrackerTable(report.result)}
                    {this.renderKeyDesignIssueTable(report.result)}
                    {this.renderDesignPerformanceRating(report.result)}
                    {this.renderCategoryRating(report.result)}
                    {this.renderZoneRating(report.result)}
                </div>
            );
        }
    };

    renderContent = (): React.ReactElement | undefined => {
        return (
            <>
                {this.renderSelector()}
                {this.renderToolbar()}
                {this.renderSaveModal()}
                {this.renderReport()}
            </>
        );
    };

    render() {
        return <LayoutComponent pageId="reports">{this.renderContent()}</LayoutComponent>;
    }
}
export default injectIntl(DesignRatingReportPage);

type ParamsType = { id: string };
interface Props extends RouteComponentProps<ParamsType>, WrappedComponentProps {}

interface State {
    hubs: Hub[];
    hub?: Hub;
    projects: Project[];
    project?: Project;
    checklists: Checklist[];
    checklist?: Checklist;
    report?: DesignRatingReport;
    saveModalVisible?: boolean;
    loading?: 'loadingHubs' | 'loadingProjects' | 'loadingChecklists' | 'exporting' | 'loading';
}
